import React from "react";
import { Box, Container, Heading, Text, VStack, Table, Thead, Tbody, Tr, Th, Td, Badge, Icon, SimpleGrid, HStack, Button } from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle, FaStar } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarResourcesComparisons: React.FC = () => {
  const systems = [
    {
      name: "Budget Saver",
      price: "$5,500",
      efficiency: "17.5%",
      warranty: "10 years",
      panelType: "Polycrystalline",
      output: "6.6kW",
      features: {
        tierOnePanels: false,
        premiumInverter: false,
        monitoring: true,
        extendedWarranty: false,
        batteryReady: true
      },
      bestFor: "Budget-conscious homeowners with moderate energy needs"
    },
    {
      name: "Performance Plus",
      price: "$8,500",
      efficiency: "20.5%",
      warranty: "15 years",
      panelType: "Monocrystalline",
      output: "8.8kW",
      features: {
        tierOnePanels: true,
        premiumInverter: true,
        monitoring: true,
        extendedWarranty: false,
        batteryReady: true
      },
      bestFor: "Families seeking optimal efficiency and performance",
      popular: true
    },
    {
      name: "Premium Elite",
      price: "$12,500",
      efficiency: "22.8%",
      warranty: "25 years",
      panelType: "N-Type IBC",
      output: "10.5kW",
      features: {
        tierOnePanels: true,
        premiumInverter: true,
        monitoring: true,
        extendedWarranty: true,
        batteryReady: true
      },
      bestFor: "High-end installations with maximum efficiency requirements"
    },
    {
      name: "Commercial Pro",
      price: "$28,500",
      efficiency: "21.5%",
      warranty: "20 years",
      panelType: "Bifacial",
      output: "25kW",
      features: {
        tierOnePanels: true,
        premiumInverter: true,
        monitoring: true,
        extendedWarranty: true,
        batteryReady: true
      },
      bestFor: "Small to medium businesses with high energy consumption"
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/resources/comparisons"
        fallbackTitle="Solar System Comparisons"
        fallbackDescription="Compare different solar systems and find the perfect fit for your needs"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Resources
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Solar System Comparisons
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Find the perfect solar solution by comparing features, pricing, and performance
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Comparison Table */}
      <Container maxW="container.xl" py={16}>
        <Box overflowX="auto" bg="white" borderRadius="xl" shadow="lg" border="1px solid" borderColor="gray.200">
          <Table variant="simple" size="lg">
            <Thead bg="gray.50">
              <Tr>
                <Th fontSize="md" py={6}>Feature</Th>
                {systems.map((system) => (
                  <Th key={system.name} textAlign="center" position="relative">
                    {system.popular && (
                      <Badge
                        position="absolute"
                        top={2}
                        right={2}
                        bg={SOLAR_GOLD}
                        color="white"
                        fontSize="xs"
                        px={2}
                        py={1}
                      >
                        Most Popular
                      </Badge>
                    )}
                    <VStack spacing={1}>
                      <Text fontSize="lg" fontWeight="bold" color={NAVY}>
                        {system.name}
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                        {system.price}
                      </Text>
                    </VStack>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              <Tr bg="white">
                <Td fontWeight="semibold">System Size</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center" fontSize="lg" fontWeight="bold" color={NAVY}>
                    {system.output}
                  </Td>
                ))}
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="semibold">Panel Efficiency</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center" fontSize="md">
                    {system.efficiency}
                  </Td>
                ))}
              </Tr>
              <Tr bg="white">
                <Td fontWeight="semibold">Panel Type</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center" fontSize="md">
                    {system.panelType}
                  </Td>
                ))}
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="semibold">Warranty Period</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center" fontSize="md" fontWeight="semibold">
                    {system.warranty}
                  </Td>
                ))}
              </Tr>
              <Tr bg="white">
                <Td fontWeight="semibold">Tier 1 Panels</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center">
                    <Icon
                      as={system.features.tierOnePanels ? FaCheckCircle : FaTimesCircle}
                      color={system.features.tierOnePanels ? SOLAR_GOLD : "gray.300"}
                      boxSize={6}
                    />
                  </Td>
                ))}
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="semibold">Premium Inverter</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center">
                    <Icon
                      as={system.features.premiumInverter ? FaCheckCircle : FaTimesCircle}
                      color={system.features.premiumInverter ? SOLAR_GOLD : "gray.300"}
                      boxSize={6}
                    />
                  </Td>
                ))}
              </Tr>
              <Tr bg="white">
                <Td fontWeight="semibold">Real-time Monitoring</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center">
                    <Icon
                      as={system.features.monitoring ? FaCheckCircle : FaTimesCircle}
                      color={system.features.monitoring ? SOLAR_GOLD : "gray.300"}
                      boxSize={6}
                    />
                  </Td>
                ))}
              </Tr>
              <Tr bg="gray.50">
                <Td fontWeight="semibold">Extended Warranty</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center">
                    <Icon
                      as={system.features.extendedWarranty ? FaCheckCircle : FaTimesCircle}
                      color={system.features.extendedWarranty ? SOLAR_GOLD : "gray.300"}
                      boxSize={6}
                    />
                  </Td>
                ))}
              </Tr>
              <Tr bg="white">
                <Td fontWeight="semibold">Battery Ready</Td>
                {systems.map((system) => (
                  <Td key={system.name} textAlign="center">
                    <Icon
                      as={system.features.batteryReady ? FaCheckCircle : FaTimesCircle}
                      color={system.features.batteryReady ? SOLAR_GOLD : "gray.300"}
                      boxSize={6}
                    />
                  </Td>
                ))}
              </Tr>
            </Tbody>
          </Table>
        </Box>
      </Container>

      {/* Best For Scenarios */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={10} textAlign="center">Best For Your Situation</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {systems.map((system) => (
              <Box
                key={system.name}
                p={6}
                bg="white"
                borderRadius="xl"
                border="2px solid"
                borderColor={SOLAR_GOLD}
                position="relative"
                _hover={{ transform: "translateY(-4px)", shadow: "lg" }}
                transition="all 0.3s"
              >
                {system.popular && (
                  <Icon
                    as={FaStar}
                    position="absolute"
                    top={3}
                    right={3}
                    color={SOLAR_GOLD}
                    boxSize={5}
                  />
                )}
                <VStack align="start" spacing={4}>
                  <Badge bg={NAVY} color="white" fontSize="sm" px={3} py={1}>
                    {system.name}
                  </Badge>
                  <Text fontSize="md" color="gray.700" minH="60px">
                    {system.bestFor}
                  </Text>
                  <Button
                    bg={NAVY}
                    color="white"
                    _hover={{ bg: SOLAR_GOLD_HOVER }}
                    size="sm"
                    width="full"
                  >
                    Get Quote
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box
          bg={brandConfig.colors.primary}
          color="white"
          p={12}
          borderRadius="2xl"
          textAlign="center"
        >
          <VStack spacing={6}>
            <Heading size="xl">Not Sure Which System Is Right?</Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Our solar experts can help you choose the perfect system based on your energy needs, budget, and long-term goals.
            </Text>
            <HStack spacing={4}>
              <Button bg={SOLAR_GOLD} color="white" _hover={{ bg: SOLAR_GOLD_HOVER }} size="lg">
                Speak to an Expert
              </Button>
              <Button variant="outline" colorScheme="whiteAlpha" size="lg">
                Use Calculator
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarResourcesComparisons;
